<script setup lang="ts">
/**
 * Read-only field display for the ViewerSheet.
 * Renders field values as formatted text instead of form inputs.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Badge } from '@/components/ui/badge/index';
import { formatDateTimeByTag, formatDayByTag } from '@ja/core-ui/lib/date';

import { useViewerSheet } from '../composables/useViewerSheet';
import type { FieldConfig } from '../types';

import DurationValue from './DurationValue.vue';

const props = defineProps<{
    field: FieldConfig;
    value: unknown;
    trField: (key: string) => string;
    trBadge?: (value: unknown, fallback: string) => string;
    item?: Record<string, unknown> | null;
}>();

const { t, locale } = useI18n();

const label = computed(() => props.trField(props.field.key));

const isBadgeType = computed(() =>
    props.field.type === 'badge' || props.field.type === 'select' || props.field.type === 'toggle-group',
);

const sheetState = useViewerSheet();

const linkedRelationTarget = computed<{ model: string; id: string } | null>(() => {
    if (props.field.type !== 'linkedRelation') return null;
    const meta = (props.field.metadata ?? {}) as { modelKey?: string; idKey?: string };
    if (!meta.modelKey || !meta.idKey || !props.item) return null;
    const id = props.item[meta.idKey];
    if (id === null || id === undefined || id === '') return null;
    return { model: meta.modelKey, id: String(id) };
});

function openLinkedRelation(): void {
    if (!linkedRelationTarget.value) return;
    sheetState.openViewerSheet(linkedRelationTarget.value.model, linkedRelationTarget.value.id);
}

const displayValue = computed(() => {
    const val = props.value;

    if (val === null || val === undefined || val === '') {
        return '—';
    }

    // Boolean types
    if (props.field.type === 'boolean' || props.field.type === 'switch' || props.field.type === 'checkbox') {
        return val ? t('crud.yes') : t('crud.no');
    }

    // Date
    if (props.field.type === 'date' && val) {
        return formatDayByTag(String(val), locale.value);
    }

    // DateTime
    if (props.field.type === 'datetime' && val) {
        return formatDateTimeByTag(String(val), locale.value);
    }

    // Time
    if (props.field.type === 'time' && val) {
        return String(val);
    }

    // JSON field type — always pretty-print
    if (props.field.type === 'json') {
        return typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val);
    }

    // Array values (multi-select, etc.)
    if (Array.isArray(val)) {
        if (val.length === 0) return '—';
        // Arrays of objects need JSON serialization, not .join()
        if (typeof val[0] === 'object' && val[0] !== null) {
            return JSON.stringify(val, null, 2);
        }
        return val.join(', ');
    }

    // Select/badge — translate via trBadge, fall back to option label, then raw value
    if (isBadgeType.value) {
        const raw = String(val);
        if (props.trBadge) {
            const translated = props.trBadge(raw, raw);
            if (translated !== raw) return translated;
        }
        if (props.field.options) {
            const opt = (props.field.options as Array<{ value: string; label?: string }>)
                .find((o) => String(o.value) === raw);
            if (opt?.label) return opt.label;
        }
        return raw;
    }

    return String(val);
});

/**
 * Clickable href for email / phone / url field types so contact values
 * render as mailto:, tel: or external links instead of plain text.
 */
const linkHref = computed<string | null>(() => {
    const val = props.value;
    if (val === null || val === undefined || val === '') return null;
    const s = String(val).trim();

    switch (props.field.type) {
        case 'email':
            return `mailto:${s}`;
        case 'phone':
        case 'tel': {
            const tel = s.replace(/[^\d+]/g, '');
            return tel ? `tel:${tel}` : null;
        }
        case 'url':
        case 'website':
            return /^https?:\/\//i.test(s) ? s : `https://${s}`;
        default:
            return null;
    }
});

const isExternalLink = computed(() => props.field.type === 'url' || props.field.type === 'website');
</script>

<template>
    <div :class="['min-w-0', field.colspan === 2 || field.type === 'json' ? 'col-span-full' : '']">
        <dt class="text-muted-foreground mb-1 text-xs font-medium">
            {{ label }}
        </dt>
        <dd class="text-foreground text-sm leading-normal">
            <!-- Badge display for select/badge fields -->
            <template v-if="isBadgeType && value !== null && value !== undefined && value !== ''">
                <template v-if="Array.isArray(value)">
                    <div class="flex flex-wrap gap-1">
                        <Badge v-for="(v, i) in value" :key="i" variant="secondary" class="text-xs">
                            {{ trBadge ? trBadge(v, String(v)) : v }}
                        </Badge>
                    </div>
                </template>
                <Badge v-else variant="secondary" class="text-xs">
                    {{ displayValue }}
                </Badge>
            </template>
            <!-- Boolean as badge -->
            <template v-else-if="field.type === 'switch' || field.type === 'checkbox' || field.type === 'boolean'">
                <Badge :variant="value ? 'default' : 'outline'" class="text-xs">
                    {{ displayValue }}
                </Badge>
            </template>
            <!-- Duration display with mode toggle -->
            <DurationValue
                v-else-if="field.type === 'duration'"
                :model-value="(value as number | null | undefined)"
                :default-mode="(field.metadata?.defaultMode as 'hhmm' | 'decimal' | 'minutes' | undefined) ?? 'hhmm'"
            />
            <!-- Linked relation: opens the related record in the ViewerSheet -->
            <template v-else-if="field.type === 'linkedRelation'">
                <button
                    v-if="linkedRelationTarget"
                    type="button"
                    class="text-primary cursor-pointer text-left hover:underline"
                    @click.stop="openLinkedRelation"
                >
                    {{ displayValue }}
                </button>
                <span v-else class="text-muted-foreground">—</span>
            </template>
            <!-- Empty state -->
            <span v-else-if="displayValue === '—'" class="text-muted-foreground">—</span>
            <!-- JSON display -->
            <pre v-else-if="field.type === 'json'" class="bg-muted text-foreground max-h-64 overflow-auto rounded-md p-3 text-xs leading-relaxed whitespace-pre-wrap">{{ displayValue }}</pre>
            <!-- Link display for email / phone / url field types -->
            <a
                v-else-if="linkHref"
                :href="linkHref"
                :target="isExternalLink ? '_blank' : undefined"
                :rel="isExternalLink ? 'noopener noreferrer' : undefined"
                class="text-primary break-words hover:underline"
                @click.stop
            >{{ displayValue }}</a>
            <!-- Text display -->
            <span v-else class="break-words">{{ displayValue }}</span>
        </dd>
    </div>
</template>
